import {
  Box,
  CardActionArea,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material"
import {
  Chip,
  Link,
  MarkdownDisplay,
  formatDateWithDots,
} from "@pharmupp/p3-front-commons"
import { AlertCircle, Check, ChevronRight } from "react-feather"
import { EventCancelledChip } from "../../commons/EventCancelledChip"

export type EventItemProps = {
  eventPath: string
  id: number
  startDate: string
  title: string
  description?: string
  cancelled?: boolean
  isRegistered?: boolean
}

export type EventData = Omit<EventItemProps, "eventPath">

export const EventItem = ({
  eventPath,
  id,
  startDate,
  title,
  description,
  cancelled,
  isRegistered,
}: EventItemProps) => {
  return (
    <Paper component={Box} width="100%" height="100%" borderRadius={0}>
      <CardActionArea
        component={Link}
        to={`${eventPath}${String(id)}`}
        sx={{ height: "100%", padding: 2 }}
      >
        <Stack spacing={2} height="100%">
          <DateChip startDate={startDate} cancelled={cancelled} />
          <Stack justifyContent="space-between" height="100%" spacing={2}>
            <Stack>
              <EventTitle>{title}</EventTitle>
              {!!description && (
                <Description mt={1}>
                  <MarkdownDisplay ignoreFormatting>{description}</MarkdownDisplay>
                </Description>
              )}
            </Stack>
            {isRegistered ? <SubscribedChip /> : <UpcomingFooter />}
          </Stack>
        </Stack>
      </CardActionArea>
    </Paper>
  )
}

const DateChip = ({
  startDate,
  cancelled,
}: {
  startDate: string
  cancelled?: boolean
}) => {
  const { typography, palette } = useTheme()

  return (
    <Stack
      direction="row"
      spacing={1}
      rowGap={0.5}
      alignItems="center"
      flexWrap="wrap"
    >
      <Chip
        label={formatDateWithDots(startDate)}
        size="small"
        fontColor="common.white"
        backgroundColor={cancelled ? "common.darkBlue" : "primary.dark"}
        sx={{
          fontSize: typography.pxToRem(11),
          textDecoration: cancelled ? "line-through" : "none",
          textDecorationColor: cancelled ? palette.common.salmon : "none",
        }}
      />
      {cancelled && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <EventCancelledChip />
          <AlertCircle size={19} color={palette.common.red} />
        </Stack>
      )}
    </Stack>
  )
}

const EventTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: "bold",
  color: theme.palette.common.darkBlue,
}))

const Description = styled(Box)({
  fontSize: "0.75rem",
  color: "common.darkBlue",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: "2",
  lineClamp: "2",
})

const SubscribedChip = () => {
  const { palette } = useTheme()

  return (
    <Chip
      variant="outlined"
      fontColor="success.main"
      icon={<Check size={14} color={palette.success.main} />}
      label="Inscrit"
      size="medium"
      sx={{
        alignSelf: "flex-start",
        borderRadius: "100px",
        textTransform: "none",
      }}
    />
  )
}

const UpcomingFooter = () => {
  const { typography } = useTheme()

  return (
    <Stack direction="row" color="common.electricBlue">
      <Typography
        color="inherit"
        fontWeight="600"
        sx={{ fontSize: typography.pxToRem(11) }}
      >
        S&apos;inscrire
      </Typography>
      <ChevronRight size="1.2em" />
    </Stack>
  )
}
